import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";

/**
 * SEO Performance Monitor Component
 * Tracks Core Web Vitals and other performance metrics important for SEO
 */
const SEOPerformanceMonitor = ({
  onMetricUpdate = () => {},
  enableReporting = true,
  debug = false,
}) => {
  const [metrics, setMetrics] = useState({
    cls: null,
    fid: null,
    fcp: null,
    lcp: null,
    ttfb: null,
    inp: null,
  });

  const [performanceScore, setPerformanceScore] = useState(null);
  const [isWebVitalsLoaded, setIsWebVitalsLoaded] = useState(false);

  // Load Web Vitals library
  useEffect(() => {
    const loadWebVitals = async () => {
      try {
        const webVitals = await import("web-vitals");
        setIsWebVitalsLoaded(true);

        // Core Web Vitals
        webVitals.getCLS(handleMetric);
        webVitals.getFID(handleMetric);
        webVitals.getFCP(handleMetric);
        webVitals.getLCP(handleMetric);
        webVitals.getTTFB(handleMetric);

        // New metric (if available)
        if (webVitals.getINP) {
          webVitals.getINP(handleMetric);
        }

        if (debug) {
          console.log("Web Vitals library loaded successfully");
        }
      } catch (error) {
        if (debug) {
          console.warn("Failed to load Web Vitals library:", error);
        }
        // Fallback to manual performance monitoring
        initializeManualTracking();
      }
    };

    loadWebVitals();
  }, []);

  // Handle metric updates
  const handleMetric = (metric) => {
    const metricData = {
      name: metric.name,
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
      id: metric.id,
      timestamp: Date.now(),
    };

    setMetrics((prev) => ({
      ...prev,
      [metric.name.toLowerCase()]: metricData,
    }));

    // Calculate performance score
    updatePerformanceScore(metricData);

    // Send to analytics
    if (enableReporting) {
      reportToAnalytics(metricData);
    }

    // Callback for parent component
    onMetricUpdate(metricData);

    if (debug) {
      console.log("Web Vital metric:", metricData);
    }
  };

  // Calculate overall performance score
  const updatePerformanceScore = (newMetric) => {
    setMetrics((currentMetrics) => {
      const updatedMetrics = {
        ...currentMetrics,
        [newMetric.name.toLowerCase()]: newMetric,
      };

      // Calculate score based on Core Web Vitals thresholds
      let score = 0;
      let totalMetrics = 0;

      // CLS scoring (good: ≤0.1, needs improvement: ≤0.25, poor: >0.25)
      if (updatedMetrics.cls) {
        totalMetrics++;
        if (updatedMetrics.cls.value <= 0.1) score += 100;
        else if (updatedMetrics.cls.value <= 0.25) score += 75;
        else score += 50;
      }

      // LCP scoring (good: ≤2.5s, needs improvement: ≤4s, poor: >4s)
      if (updatedMetrics.lcp) {
        totalMetrics++;
        if (updatedMetrics.lcp.value <= 2500) score += 100;
        else if (updatedMetrics.lcp.value <= 4000) score += 75;
        else score += 50;
      }

      // FID/INP scoring (good: ≤100ms, needs improvement: ≤300ms, poor: >300ms)
      const interactionMetric = updatedMetrics.inp || updatedMetrics.fid;
      if (interactionMetric) {
        totalMetrics++;
        if (interactionMetric.value <= 100) score += 100;
        else if (interactionMetric.value <= 300) score += 75;
        else score += 50;
      }

      if (totalMetrics > 0) {
        const calculatedScore = Math.round(score / totalMetrics);
        setPerformanceScore(calculatedScore);
      }

      return updatedMetrics;
    });
  };

  // Report metrics to analytics services
  const reportToAnalytics = (metric) => {
    // Google Analytics 4
    if (window.gtag) {
      window.gtag("event", metric.name, {
        event_category: "Web Vitals",
        event_label: metric.id,
        value: Math.round(
          metric.name === "CLS" ? metric.value * 1000 : metric.value,
        ),
        custom_parameter_1: metric.rating,
        non_interaction: true,
      });
    }

    // Google Tag Manager
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "web_vital",
        metric_name: metric.name,
        metric_value: metric.value,
        metric_rating: metric.rating,
        metric_id: metric.id,
      });
    }

    // Custom reporting endpoint (if needed)
    if (window.customAnalytics && window.customAnalytics.trackPerformance) {
      window.customAnalytics.trackPerformance(metric);
    }
  };

  // Fallback manual performance tracking
  const initializeManualTracking = () => {
    // Manual FCP tracking
    const paintEntries = performance.getEntriesByType("paint");
    const fcpEntry = paintEntries.find(
      (entry) => entry.name === "first-contentful-paint",
    );
    if (fcpEntry) {
      handleMetric({
        name: "FCP",
        value: fcpEntry.startTime,
        rating:
          fcpEntry.startTime <= 1800
            ? "good"
            : fcpEntry.startTime <= 3000
              ? "needs-improvement"
              : "poor",
        id: "manual-fcp",
      });
    }

    // Manual TTFB tracking
    const navigationEntry = performance.getEntriesByType("navigation")[0];
    if (navigationEntry) {
      const ttfb = navigationEntry.responseStart - navigationEntry.requestStart;
      handleMetric({
        name: "TTFB",
        value: ttfb,
        rating:
          ttfb <= 800 ? "good" : ttfb <= 1800 ? "needs-improvement" : "poor",
        id: "manual-ttfb",
      });
    }

    // Manual LCP observer
    if ("PerformanceObserver" in window) {
      try {
        const lcpObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          const lastEntry = entries[entries.length - 1];
          if (lastEntry) {
            handleMetric({
              name: "LCP",
              value: lastEntry.startTime,
              rating:
                lastEntry.startTime <= 2500
                  ? "good"
                  : lastEntry.startTime <= 4000
                    ? "needs-improvement"
                    : "poor",
              id: "manual-lcp",
            });
          }
        });
        lcpObserver.observe({
          type: "largest-contentful-paint",
          buffered: true,
        });
      } catch (error) {
        if (debug) {
          console.warn("Manual LCP tracking failed:", error);
        }
      }
    }

    // Manual CLS tracking
    if ("PerformanceObserver" in window) {
      try {
        let clsValue = 0;
        const clsObserver = new PerformanceObserver((entryList) => {
          for (const entry of entryList.getEntries()) {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          }
          handleMetric({
            name: "CLS",
            value: clsValue,
            rating:
              clsValue <= 0.1
                ? "good"
                : clsValue <= 0.25
                  ? "needs-improvement"
                  : "poor",
            id: "manual-cls",
          });
        });
        clsObserver.observe({ type: "layout-shift", buffered: true });
      } catch (error) {
        if (debug) {
          console.warn("Manual CLS tracking failed:", error);
        }
      }
    }
  };

  // Monitor resource loading performance
  useEffect(() => {
    const observeResources = () => {
      if ("PerformanceObserver" in window) {
        const resourceObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            // Track slow resources
            if (entry.duration > 1000) {
              if (window.gtag) {
                window.gtag("event", "slow_resource", {
                  event_category: "Performance",
                  event_label: entry.name,
                  value: Math.round(entry.duration),
                });
              }
            }
          });
        });

        resourceObserver.observe({ entryTypes: ["resource"] });

        // Cleanup
        return () => resourceObserver.disconnect();
      }
    };

    const cleanup = observeResources();
    return cleanup;
  }, []);

  // Monitor long tasks
  useEffect(() => {
    if ("PerformanceObserver" in window) {
      try {
        const longTaskObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (window.gtag) {
              window.gtag("event", "long_task", {
                event_category: "Performance",
                event_label: "blocking_time",
                value: Math.round(entry.duration),
              });
            }
          });
        });

        longTaskObserver.observe({ entryTypes: ["longtask"] });

        return () => longTaskObserver.disconnect();
      } catch (error) {
        if (debug) {
          console.warn("Long task observer not supported:", error);
        }
      }
    }
  }, []);

  // Get performance grade
  const getPerformanceGrade = () => {
    if (performanceScore === null) return "N/A";
    if (performanceScore >= 90) return "A";
    if (performanceScore >= 80) return "B";
    if (performanceScore >= 70) return "C";
    if (performanceScore >= 60) return "D";
    return "F";
  };

  // Format metric value for display
  const formatMetricValue = (metric) => {
    if (!metric) return "N/A";

    switch (metric.name) {
      case "CLS":
        return metric.value.toFixed(3);
      case "FID":
      case "INP":
      case "FCP":
      case "LCP":
      case "TTFB":
        return `${Math.round(metric.value)}ms`;
      default:
        return metric.value;
    }
  };

  return (
    <>
      <Helmet>
        {/* Performance optimization hints */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous"
        />
        <link rel="dns-prefetch" href="//www.google-analytics.com" />
        <link rel="dns-prefetch" href="//www.googletagmanager.com" />

        {/* Resource hints for better performance */}
        <link rel="prefetch" href="/syllabus" />

        {/* Critical CSS inlining hint */}
        <style>
          {`
            /* Critical above-the-fold styles */
            .critical-content { visibility: visible; }
          `}
        </style>
      </Helmet>

      {/* Performance monitoring script */}
      <Helmet>
        <script>
          {`
            // Monitor page load performance
            window.addEventListener('load', function() {
              const perfData = performance.timing;
              const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;

              if (window.gtag) {
                gtag('event', 'page_load_time', {
                  event_category: 'Performance',
                  event_label: window.location.pathname,
                  value: pageLoadTime,
                });
              }
            });

            // Monitor memory usage (if supported)
            if ('memory' in performance) {
              setInterval(function() {
                const memInfo = performance.memory;
                if (memInfo.usedJSHeapSize / memInfo.jsHeapSizeLimit > 0.9) {
                  if (window.gtag) {
                    gtag('event', 'high_memory_usage', {
                      event_category: 'Performance',
                      value: Math.round((memInfo.usedJSHeapSize / memInfo.jsHeapSizeLimit) * 100),
                    });
                  }
                }
              }, 30000); // Check every 30 seconds
            }

            // Monitor network connection
            if ('connection' in navigator) {
              const connection = navigator.connection;
              if (window.gtag) {
                gtag('event', 'network_info', {
                  event_category: 'Performance',
                  custom_parameter_1: connection.effectiveType,
                  custom_parameter_2: connection.downlink,
                });
              }
            }
          `}
        </script>
      </Helmet>

      {/* Development debug panel */}
      {debug && (
        <div
          style={{
            position: "fixed",
            bottom: "10px",
            right: "10px",
            background: "rgba(0, 0, 0, 0.8)",
            color: "white",
            padding: "10px",
            borderRadius: "5px",
            fontSize: "12px",
            zIndex: 9999,
            fontFamily: "monospace",
            minWidth: "200px",
          }}
        >
          <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
            Performance Score: {performanceScore || "Loading..."} (
            {getPerformanceGrade()})
          </div>
          <div>
            CLS: {formatMetricValue(metrics.cls)}{" "}
            {metrics.cls?.rating && `(${metrics.cls.rating})`}
          </div>
          <div>
            LCP: {formatMetricValue(metrics.lcp)}{" "}
            {metrics.lcp?.rating && `(${metrics.lcp.rating})`}
          </div>
          <div>
            FID: {formatMetricValue(metrics.fid)}{" "}
            {metrics.fid?.rating && `(${metrics.fid.rating})`}
          </div>
          <div>
            FCP: {formatMetricValue(metrics.fcp)}{" "}
            {metrics.fcp?.rating && `(${metrics.fcp.rating})`}
          </div>
          <div>
            TTFB: {formatMetricValue(metrics.ttfb)}{" "}
            {metrics.ttfb?.rating && `(${metrics.ttfb.rating})`}
          </div>
          {metrics.inp && (
            <div>
              INP: {formatMetricValue(metrics.inp)} ({metrics.inp.rating})
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default SEOPerformanceMonitor;

// Export performance utilities
export const getPerformanceMetrics = () => {
  return new Promise((resolve) => {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => {
        resolve({
          navigation: performance.getEntriesByType("navigation")[0],
          paint: performance.getEntriesByType("paint"),
          resources: performance.getEntriesByType("resource"),
          memory: performance.memory,
        });
      });
    } else {
      setTimeout(() => {
        resolve({
          navigation: performance.getEntriesByType("navigation")[0],
          paint: performance.getEntriesByType("paint"),
          resources: performance.getEntriesByType("resource"),
          memory: performance.memory,
        });
      }, 0);
    }
  });
};

export const trackCustomPerformanceMetric = (
  name,
  value,
  category = "Performance",
) => {
  if (window.gtag) {
    window.gtag("event", "custom_performance_metric", {
      event_category: category,
      event_label: name,
      value: Math.round(value),
    });
  }

  if (window.dataLayer) {
    window.dataLayer.push({
      event: "custom_performance_metric",
      metric_name: name,
      metric_value: value,
      metric_category: category,
    });
  }
};
